import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import type { TokenUsageStats } from '../../services/api';
import { contentColumnPadding } from '../../theme/constants';
import { POPOVER_WIDTH, UsagePopover, type PopoverPlacement } from './UsagePopover';
import {
  METER_COLORS,
  formatAbbrev,
  formatResetCountdown,
  getMeterPercent,
  getMeterState,
} from './utils';
import TokenPlansModal from '../../../askVantage/components/TokenPlansModal';

type Variant = 'compact' | 'expanded';

interface TokenMeterProps {
  stats: TokenUsageStats | null;
  loading: boolean;
  isDemo: boolean;
  variant: Variant;
  /**
   * Cross-fades the meter in/out. Used by `AskV2Container` to animate between
   * the hero (expanded) and chat (compact) variants without DOM flicker.
   * Defaults to true when omitted.
   */
  show?: boolean;
}

export const TokenMeter: React.FC<TokenMeterProps> = ({
  stats,
  loading,
  isDemo,
  variant,
  show = true,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<React.CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click. We include the trigger ref to avoid the same
  // click immediately re-opening it after toggling.
  useEffect(() => {
    if (!popoverOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);

  // Close the popover whenever we cross-fade out. Prevents it from hanging
  // visually during the hero↔chat transition.
  useEffect(() => {
    if (!show && popoverOpen) setPopoverOpen(false);
  }, [show, popoverOpen]);

  const handleToggle = useCallback(() => setPopoverOpen((v) => !v), []);
  const handleBuyClick = useCallback(() => {
    setPopoverOpen(false);
    setPlansModalOpen(true);
  }, []);

  // Depends only on `variant`, so it is safe to compute above the early return
  // below — the hooks that follow must run unconditionally.
  const popoverPlacement: PopoverPlacement = variant === 'expanded' ? 'above' : 'below';

  // The popover is portalled into document.body, so it can no longer inherit
  // offsets from this Wrapper. Derive them from the trigger's viewport rect.
  const updatePopoverPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const GAP = 10;
    const EDGE = 8;
    // Right-align with the trigger (matches the previous `right: 0`), clamped so
    // the panel never runs off-screen on narrow viewports.
    const left = Math.min(
      Math.max(EDGE, rect.right - POPOVER_WIDTH),
      Math.max(EDGE, window.innerWidth - POPOVER_WIDTH - EDGE)
    );
    setPopoverPos(
      popoverPlacement === 'above'
        ? { left, bottom: window.innerHeight - rect.top + GAP }
        : { left, top: rect.bottom + GAP }
    );
  }, [popoverPlacement]);

  // Position before paint to avoid a first-frame flash at the wrong spot, and
  // keep it anchored while the page scrolls or resizes underneath.
  useLayoutEffect(() => {
    if (!popoverOpen) return;
    updatePopoverPosition();
    window.addEventListener('scroll', updatePopoverPosition, true);
    window.addEventListener('resize', updatePopoverPosition);
    return () => {
      window.removeEventListener('scroll', updatePopoverPosition, true);
      window.removeEventListener('resize', updatePopoverPosition);
    };
  }, [popoverOpen, updatePopoverPosition]);

  // Hide entirely while loading the first payload — prevents a "0 tokens"
  // flash. Cross-fade container still animates layout smoothly.
  if (loading && !stats && !isDemo) {
    return <Wrapper $show={show} $variant={variant} />;
  }

  const state = getMeterState(stats, isDemo);
  const color = METER_COLORS[state];
  const percent = getMeterPercent(stats, state, isDemo);
  const resets = isDemo ? '' : formatResetCountdown(stats?.daily_quota_resets_at);
  // Always show total (daily + purchased) so users don't see "0 left" while
  // they still have purchased credits available.
  const totalAvailable = stats?.total_available_tokens ?? 0;

  return (
    <Wrapper $show={show} $variant={variant}>
      {variant === 'compact' ? (
        <CompactPill
          ref={triggerRef}
          onClick={handleToggle}
          $state={state}
          aria-label="Token usage"
          aria-expanded={popoverOpen}
          type="button"
        >
          <PillBar $state={state}>
            {!isDemo && <PillFill style={{ width: `${percent}%`, background: color }} />}
          </PillBar>
          <PillLabel>
            {isDemo
              ? 'Unlimited'
              : state === 'exhausted'
                ? 'Buy tokens'
                : `${formatAbbrev(totalAvailable)} left`}
          </PillLabel>
        </CompactPill>
      ) : (
        <ExpandedRow
          ref={triggerRef}
          onClick={handleToggle}
          $state={state}
          aria-label="Token usage"
          aria-expanded={popoverOpen}
          type="button"
        >
          {!isDemo && (
            <ExpandedBar $state={state}>
              <ExpandedFill style={{ width: `${percent}%`, background: color }} />
            </ExpandedBar>
          )}
          <ExpandedText>
            {isDemo ? (
              <>
                <strong>Unlimited</strong> tokens
              </>
            ) : state === 'exhausted' ? (
              <ExpandedExhausted>Out of tokens — buy more</ExpandedExhausted>
            ) : state === 'paid' ? (
              <>
                <strong>{formatAbbrev(totalAvailable)}</strong> left on paid credits
                {resets && <> · Daily resets in {resets}</>}
              </>
            ) : (
              <>
                <strong>{formatAbbrev(totalAvailable)}</strong> tokens left
                {resets && <> · Resets in {resets}</>}
              </>
            )}
          </ExpandedText>
        </ExpandedRow>
      )}

      {popoverOpen &&
        popoverPos &&
        createPortal(
          <UsagePopover
            ref={popoverRef}
            stats={stats}
            isDemo={isDemo}
            placement={popoverPlacement}
            onBuyClick={handleBuyClick}
            style={popoverPos}
          />,
          document.body
        )}

      <TokenPlansModal
        show={plansModalOpen}
        onClose={() => setPlansModalOpen(false)}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $show: boolean; $variant: Variant }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateY(${({ $show }) => ($show ? '0' : '-4px')});
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  /* Raise the trigger above the MessageInput textarea (which uses z-index: 1
     to sit above its AnimatedPlaceholder sibling). Note this no longer affects
     the popover: it is portalled into document.body, precisely because the
     transform above creates a stacking context and the InputShell/FooterRow
     ancestors clip with overflow:hidden. */
  z-index: 10;
  /* Collapse width when hidden so the neighbouring element (e.g. disclaimer
     in the hero footer row) can recenter during the hero↔chat transition. */
  max-width: ${({ $show, $variant }) =>
    !$show ? '0' : $variant === 'expanded' ? '100%' : '320px'};
  min-width: 0;
  flex: ${({ $variant }) => ($variant === 'expanded' ? '0 1 auto' : '0 0 auto')};
  overflow: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  transition: opacity 300ms ease, transform 300ms cubic-bezier(0.22, 1, 0.36, 1),
    max-width 300ms cubic-bezier(0.22, 1, 0.36, 1);
`;

/* ── Compact variant (top bar pill) ────────────────────────────────────── */

const CompactPill = styled.button<{ $state: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 10px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $state }) =>
      $state === 'exhausted'
        ? 'rgba(216, 90, 48, 0.55)'
        : $state === 'paid'
          ? 'rgba(232, 136, 69, 0.5)'
          : $state === 'warning'
            ? 'rgba(217, 166, 12, 0.45)'
            : theme.av2.border};
  background: ${({ theme, $state }) =>
    $state === 'exhausted'
      ? 'rgba(216, 90, 48, 0.12)'
      : $state === 'paid'
        ? 'rgba(232, 136, 69, 0.10)'
        : theme.av2.primaryTint};
  color: ${({ theme }) => theme.av2.textSecondary};
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.av2.primaryBorder};
    background: ${({ theme }) => theme.av2.primaryTint};
    box-shadow: 0 2px 10px ${({ theme }) => theme.av2.shadowAccent};
  }
`;

const PillBar = styled.div<{ $state: string }>`
  width: 56px;
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.av2.borderSubtle};
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
  flex-shrink: 0;
`;

const PillFill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 420ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease;
`;

const PillLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
`;

/* ── Expanded variant (hero footer row) ────────────────────────────────── */

const ExpandedRow = styled.button<{ $state: string }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.av2.textSecondary};
  cursor: pointer;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease;

  &:hover {
    background: ${({ theme }) => theme.av2.primaryTint};
    border-color: ${({ theme }) => theme.av2.primaryBorder};
    color: ${({ theme }) => theme.av2.textSecondary};
  }
`;

const ExpandedBar = styled.div<{ $state: string }>`
  width: 72px;
  height: 5px;
  border-radius: 999px;
  background: ${({ theme }) => theme.av2.borderSubtle};
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
  flex-shrink: 0;
`;

const ExpandedFill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 420ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease;
`;

const ExpandedText = styled.span`
  font-size: 11px;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    color: ${({ theme }) => theme.av2.textPrimary};
    font-weight: 700;
  }
`;

const ExpandedExhausted = styled.span`
  color: ${({ theme }) => theme.av2.accentText};
  font-weight: 600;
`;
